import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SettingsPage.css';

/**
 * Settings Page Component
 * Container with cards for User Profile and User Management
 */
const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCard, setActiveCard] = useState('profile');

  // Determine active card based on URL
  useEffect(() => {
    if (location.pathname.includes('/admin/users')) {
      setActiveCard('users');
    } else if (location.pathname.includes('/settings/ai-analytics')) {
      setActiveCard('ai-analytics');
    } else if (location.pathname.includes('/settings/integrations')) {
      setActiveCard('integrations');
    } else {
      setActiveCard('profile');
    }
  }, [location.pathname]);

  // Handle card selection
  const handleCardSelect = (card) => {
    setActiveCard(card);
    if (card === 'profile') {
      navigate('/profile');
    } else if (card === 'users') {
      navigate('/admin/users');
    } else if (card === 'integrations') {
      navigate('/settings/integrations');
    } else {
      navigate('/settings/ai-analytics');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, card) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardSelect(card);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your profile and user accounts</p>
      </div>

      <div className="settings-cards">
        <div 
          className={`settings-card ${activeCard === 'profile' ? 'active' : ''}`}
          onClick={() => handleCardSelect('profile')}
          onKeyDown={(e) => handleKeyDown(e, 'profile')}
          tabIndex="0"
          role="button"
          aria-pressed={activeCard === 'profile'}
        >
          <div className="card-icon">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="card-content">
            <h2>User Profile</h2>
            <p>View and manage your personal information and account settings</p>
          </div>
        </div>

        <div
          className={`settings-card ${activeCard === 'users' ? 'active' : ''}`}
          onClick={() => handleCardSelect('users')}
          onKeyDown={(e) => handleKeyDown(e, 'users')}
          tabIndex="0"
          role="button"
          aria-pressed={activeCard === 'users'}
        >
          <div className="card-icon">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M9 6a3 3 0 11-6 0 3 3 0 016 0zm8 0a3 3 0 11-6 0 3 3 0 016 0zm-9 8c0-1 1-1 1-1h5s1 0 1 1v1H8v-1zm7-1c0-1 1-1 1-1h2s1 0 1 1v1h-4v-1zM1 14c0-1 1-1 1-1h2s1 0 1 1v1H1v-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="card-content">
            <h2>User Management</h2>
            <p>Manage user accounts, roles, and permissions for the system</p>
          </div>
        </div>

        <div
          className={`settings-card ${activeCard === 'ai-analytics' ? 'active' : ''}`}
          onClick={() => handleCardSelect('ai-analytics')}
          onKeyDown={(e) => handleKeyDown(e, 'ai-analytics')}
          tabIndex="0"
          role="button"
          aria-pressed={activeCard === 'ai-analytics'}
        >
          <div className="card-icon">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
          </div>
          <div className="card-content">
            <h2>AI Chat Analytics</h2>
            <p>Usage, cost, intent breakdown, and performance insights for the AI assistant</p>
          </div>
        </div>

        <div
          className={`settings-card ${activeCard === 'integrations' ? 'active' : ''}`}
          onClick={() => handleCardSelect('integrations')}
          onKeyDown={(e) => handleKeyDown(e, 'integrations')}
          tabIndex="0"
          role="button"
          aria-pressed={activeCard === 'integrations'}
        >
          <div className="card-icon">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" />
            </svg>
          </div>
          <div className="card-content">
            <h2>Integrations</h2>
            <p>Connect QuickBooks Online to power Financial Analysis with real accounting data</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
